import { ClassSchema, SpellSchema, MonsterSchema, RoomSchema, FloorSchema, ItemSchema, SetSchema, PassiveSchema, SummonSchema, BiomeSchema, TileSchema, TalentSchema, AchievementSchema, UnlockableSchema, PetSchema, RoomModifierSchema, type Content } from './schema';
import xelor from './classes/xelor.json';
import sacrieur from './classes/sacrieur.json';
import sadida from './classes/sadida.json';
import rayonObscur from './spells/rayon-obscur.json';
import aiguille from './spells/aiguille.json';
import gelure from './spells/gelure.json';
import sablier from './spells/sablier.json';
import fletrissement from './spells/fletrissement.json';
import teleportation from './spells/teleportation.json';
import poussiere from './spells/poussiere-temporelle.json';
import contre from './spells/contre.json';
import piedDuSacrieur from './spells/pied-du-sacrieur.json';
import attirance from './spells/attirance.json';
import absorption from './spells/absorption.json';
import chatimentForce from './spells/chatiment-force.json';
import chatimentVitalesque from './spells/chatiment-vitalesque.json';
import transposition from './spells/transposition.json';
import furie from './spells/furie.json';
import punition from './spells/punition.json';
import ronce from './spells/ronce.json';
import laBloqueuse from './spells/la-bloqueuse.json';
import laFolle from './spells/la-folle.json';
import laGonflable from './spells/la-gonflable.json';
import laSacrifiee from './spells/la-sacrifiee.json';
import tremblement from './spells/tremblement.json';
import ronceInsolente from './spells/ronce-insolente.json';
import poisonParalysant from './spells/poison-paralysant.json';
import larme from './spells/larme.json';
import astrub01 from './rooms/astrub-01.json';
import astrub02 from './rooms/astrub-02.json';
import astrub03 from './rooms/astrub-03.json';
import astrubBoss from './rooms/astrub-boss.json';
import astrub from './floors/astrub.json';
import incarnam from './floors/incarnam.json';
import anneauAventurier from './items/anneau-aventurier.json';
import amuletteAventurier from './items/amulette-aventurier.json';
import capeAventurier from './items/cape-aventurier.json';
import chapeauAventurier from './items/chapeau-aventurier.json';
import bottesAventurier from './items/bottes-aventurier.json';
import baguetteBoisaille from './items/baguette-boisaille.json';
import anneauBouze from './items/anneau-bouze.json';
import amuletteBouftou from './items/amulette-bouftou.json';
import capeBouffante from './items/cape-bouffante.json';
import coiffeBouftou from './items/coiffe-bouftou.json';
import boufbottes from './items/boufbottes.json';
import marteauBouftou from './items/marteau-bouftou.json';
import aventurier from './sets/aventurier.json';
import bouftouSet from './sets/bouftou.json';
import passivesXelor from './passives/xelor.json';
import passivesSacrieur from './passives/sacrieur.json';
import passivesSadida from './passives/sadida.json';
import tilesCatalog from './tiles.json';
import prairie from './biomes/prairie.json';
import champs from './biomes/champs.json';
import foret from './biomes/foret.json';
import clairiere from './biomes/clairiere.json';
import lac from './biomes/lac.json';
import cimetiere from './biomes/cimetiere.json';
import achievementsList from './achievements/achievements.json';
import unlocksList from './unlocks/unlocks.json';
import bwak from './pets/bwak.json';
import modifiersList from './modifiers/modifiers.json';

// Tous les monstres de src/data/monsters (Astrub M0/M1 + faune d'Incarnam), triés par nom de fichier.
const MONSTERS = Object.entries(import.meta.glob<{ default: unknown }>('./monsters/*.json', { eager: true })).sort(([a], [b]) => a.localeCompare(b)).map(([, m]) => m.default);

// Toutes les invocations de src/data/summons, triées par nom de fichier.
const SUMMONS = Object.entries(import.meta.glob<{ default: unknown }>('./summons/*.json', { eager: true })).sort(([a], [b]) => a.localeCompare(b)).map(([, m]) => m.default);

// Talents de la méta : tronc commun puis branche de classe (un tableau par fichier), triés par nom de fichier.
const TALENTS = Object.entries(import.meta.glob<{ default: unknown[] }>('./talents/*.json', { eager: true })).sort(([a], [b]) => a.localeCompare(b)).flatMap(([, m]) => m.default);

// Ordre des tableaux = ordre des tables de la spec (il conditionne les tirages déterministes).
const ITEMS = [anneauAventurier, amuletteAventurier, capeAventurier, chapeauAventurier, bottesAventurier, baguetteBoisaille, anneauBouze, amuletteBouftou, capeBouffante, coiffeBouftou, boufbottes, marteauBouftou];

/** Toutes les clés de tuiles (`g1-502`, `o1-40`...) citées dans un objet de données. */
export function tileKeys(o: unknown): string[] {
  if (typeof o === 'string') return /^(g1|g2|o1)-\d+$/.test(o) ? [o] : [];
  if (Array.isArray(o)) return o.flatMap(tileKeys);
  if (o && typeof o === 'object') return Object.values(o).flatMap(tileKeys);
  return [];
}

function index<T extends { id: string }>(items: T[]): Record<string, T> {
  return Object.fromEntries(items.map((i) => [i.id, i]));
}

/** Ids en double dans une liste (l'index par id écraserait silencieusement le premier). */
function duplicateIds(label: string, items: { id: string }[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const i of items) {
    if (seen.has(i.id)) out.push(`${label} ${i.id} : id en double`);
    seen.add(i.id);
  }
  return out;
}

export function validateContent(c: Content): string[] {
  const p: string[] = [];
  for (const cls of Object.values(c.classes)) {
    if (!c.spells[cls.primarySpell]) p.push(`classe ${cls.id} : sort principal inconnu ${cls.primarySpell}`);
    for (const s of cls.spells) if (!c.spells[s]) p.push(`classe ${cls.id} : sort inconnu ${s}`);
  }
  for (const f of Object.values(c.floors)) {
    // étage généré : le pool et la salle de boss désignent des sous-zones, « * » = monstre de la table de la sous-zone
    const pool = f.generator ? c.biomes : c.rooms;
    for (const r of f.roomPool) if (!pool[r]) p.push(`étage ${f.id} : salle inconnue ${r}`);
    if (!pool[f.bossRoom]) p.push(`étage ${f.id} : salle de boss inconnue ${f.bossRoom}`);
    for (const waves of [...f.encounters, f.bossWaves]) for (const w of waves) for (const g of w.monsters) {
      if (g.id === '*' ? !f.generator : !c.monsters[g.id]) p.push(`étage ${f.id} : monstre inconnu ${g.id}`);
    }
  }
  for (const s of Object.values(c.sets)) for (const i of s.items) if (!c.items[i]) p.push(`panoplie ${s.id} : objet inconnu ${i}`);
  for (const i of Object.values(c.items)) if (i.set && !c.sets[i.set]) p.push(`objet ${i.id} : panoplie inconnue ${i.set}`);
  for (const b of Object.values(c.biomes)) {
    const { spawns, ...rest } = b;
    for (const k of tileKeys(rest)) if (!c.tiles[k]) p.push(`sous-zone ${b.id} : tuile inconnue ${k}`);
    for (const s of spawns) if (!c.monsters[s.id]) p.push(`sous-zone ${b.id} : monstre inconnu ${s.id}`);
  }
  for (const sp of Object.values(c.spells)) for (const fx of sp.effects) if (fx.type === 'summon' && !c.summons[fx.summonId]) p.push(`sort ${sp.id} : invocation inconnue ${fx.summonId}`);
  // méta-progression (M2)
  const TOLERANCE = 0.01;
  const talents = Object.values(c.talents);
  for (const t of talents) {
    for (const r of t.requires) if (!c.talents[r]) p.push(`talent ${t.id} : prérequis inconnu ${r}`);
    for (const e of t.effects) if (e.type === 'startSpell' && !c.spells[e.spellId]) p.push(`talent ${t.id} : sort de départ inconnu ${e.spellId}`);
  }
  // aucun cycle de prérequis (parcours en profondeur : 1 = en cours, 2 = terminé)
  const etat: Record<string, 1 | 2> = {};
  const visite = (id: string, chemin: string[]): void => {
    if (etat[id] === 2) return;
    if (etat[id] === 1) { p.push(`talents : cycle de prérequis ${[...chemin.slice(chemin.indexOf(id)), id].join(' -> ')}`); return; }
    etat[id] = 1;
    for (const r of c.talents[id]?.requires ?? []) if (c.talents[r]) visite(r, [...chemin, id]);
    etat[id] = 2;
  };
  for (const t of talents) visite(t.id, []);
  // positions distinctes à 0,01 près, par branche (chaque branche a son onglet et donc son repère)
  for (let i = 0; i < talents.length; i++) for (let j = i + 1; j < talents.length; j++) {
    const a = talents[i]!, b = talents[j]!;
    if (a.branch === b.branch && Math.abs(a.position.x - b.position.x) < TOLERANCE && Math.abs(a.position.y - b.position.y) < TOLERANCE) p.push(`talents ${a.id} et ${b.id} : position confondue`);
  }
  for (const a of Object.values(c.achievements)) if (a.unlocks && !c.unlockables[a.unlocks]) p.push(`succès ${a.id} : débloquable inconnu ${a.unlocks}`);
  const CIBLES = { spellPool: ['sort', c.spells], set: ['panoplie', c.sets], pet: ['familier', c.pets], modifier: ['modificateur', c.modifiers] } as const;
  const parFamilier: Record<string, string[]> = {};
  for (const u of Object.values(c.unlockables)) {
    const [nom, table] = CIBLES[u.kind];
    for (const t of u.target) if (!table[t]) p.push(`débloquable ${u.id} : ${nom} inconnu ${t}`);
    if (u.kind === 'pet') for (const t of u.target) (parFamilier[t] ??= []).push(u.id);
  }
  for (const [pet, ids] of Object.entries(parFamilier)) if (ids.length > 1) p.push(`familier ${pet} : un seul débloquable attendu, trouvé ${ids.join(', ')}`);
  for (const pet of Object.values(c.pets)) if (!pet.sprite.trim()) p.push(`familier ${pet.id} : sprite vide`);
  return p;
}

export function loadContent(): Content {
  const talentList = TALENTS.map((t) => TalentSchema.parse(t));
  const content: Content = {
    classes: index([xelor, sacrieur, sadida].map((c) => ClassSchema.parse(c))),
    spells: index([rayonObscur, aiguille, gelure, sablier, fletrissement, teleportation, poussiere, contre, piedDuSacrieur, attirance, absorption, chatimentForce, chatimentVitalesque, transposition, furie, punition, ronce, laBloqueuse, laFolle, laGonflable, laSacrifiee, tremblement, ronceInsolente, poisonParalysant, larme].map((s) => SpellSchema.parse(s))),
    monsters: index(MONSTERS.map((m) => MonsterSchema.parse(m))),
    rooms: index([astrub01, astrub02, astrub03, astrubBoss].map((r) => RoomSchema.parse(r))),
    floors: index([FloorSchema.parse(astrub), FloorSchema.parse(incarnam)]),
    items: index(ITEMS.map((i) => ItemSchema.parse(i))),
    sets: index([SetSchema.parse(aventurier), SetSchema.parse(bouftouSet)]),
    passives: index([...passivesXelor, ...passivesSacrieur, ...passivesSadida].map((x) => PassiveSchema.parse(x))),
    summons: index(SUMMONS.map((x) => SummonSchema.parse(x))),
    biomes: index([prairie, champs, foret, clairiere, lac, cimetiere].map((b) => BiomeSchema.parse(b))),
    tiles: Object.fromEntries(Object.entries(tilesCatalog.tiles).map(([k, t]) => [k, TileSchema.parse(t)])),
    talents: index(talentList),
    achievements: index((achievementsList as unknown[]).map((a) => AchievementSchema.parse(a))),
    unlockables: index((unlocksList as unknown[]).map((u) => UnlockableSchema.parse(u))),
    pets: index([PetSchema.parse(bwak)]),
    modifiers: index((modifiersList as unknown[]).map((m) => RoomModifierSchema.parse(m))),
  };
  const problems = [...duplicateIds('talent', talentList), ...validateContent(content)];
  if (problems.length) throw new Error(`Contenu invalide :\n${problems.join('\n')}`);
  return content;
}
